/**
 * Shared client search helpers.
 *
 * All client-facing search inputs (Clientes, POS, Agenda, Lealtad, Formulaciones,
 * CRM) route through these helpers so the behaviour is identical across the app:
 *   • Case-insensitive
 *   • Diacritic-insensitive ("Álvaro" matches "alvaro")
 *   • Matches on first name, last name, "first last" full name, phone (digits
 *     only) and email
 *   • Whitespace-tolerant multi-token search ("juan diego" matches
 *     "Juan Diego Barrabás")
 */

// Strip diacritics and lowercase. Uses NFD normalisation which decomposes
// accented characters into their base + combining mark, then removes the marks.
export function normalizeText(input) {
  if (input === null || input === undefined) return '';
  return String(input)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks block
    .toLowerCase()
    .trim();
}

// Reduce phone to just digits so "(55) 1234-5678" matches "5512345678".
export function normalizePhone(input) {
  if (!input) return '';
  return String(input).replace(/\D+/g, '');
}

/**
 * Build a searchable haystack for a single client. Cached lazily on the client
 * object under `__search` so we don't recompute for every keystroke.
 */
function haystack(client) {
  if (!client) return { text: '', phone: '' };
  if (client.__search) return client.__search;
  const first = normalizeText(client.first_name);
  const last = normalizeText(client.last_name);
  const email = normalizeText(client.email);
  const notes = normalizeText(client.notes);
  const text = [first, last, `${first} ${last}`, `${last} ${first}`, email, notes]
    .filter(Boolean)
    .join(' | ');
  const phone = normalizePhone(client.phone);
  const cache = { text, phone };
  try { Object.defineProperty(client, '__search', { value: cache, enumerable: false }); }
  catch { /* frozen object — skip caching */ }
  return cache;
}

/**
 * Filter a list of clients by a free-text query.
 *
 * - Empty query returns the full list untouched.
 * - Multi-token queries require ALL tokens to match somewhere in the client's
 *   text OR phone haystack.
 * - Digit-only tokens preferentially match the phone digits (fast path).
 */
export function filterClients(clients, query) {
  const q = normalizeText(query);
  if (!q || !Array.isArray(clients)) return clients || [];
  const tokens = q.split(/\s+/).filter(Boolean);
  if (!tokens.length) return clients;

  return clients.filter((c) => {
    const { text, phone } = haystack(c);
    return tokens.every((tok) => {
      // Digit-only token → look at phone first, then fall through to text.
      if (/^\d+$/.test(tok)) return phone.includes(tok) || text.includes(tok);
      return text.includes(tok);
    });
  });
}

/**
 * Utility: display name that mirrors the rest of the UI ("First Last").
 */
export function clientDisplayName(c) {
  if (!c) return '';
  const first = (c.first_name || '').trim();
  const last = (c.last_name || '').trim();
  return `${first} ${last}`.trim() || c.email || 'Cliente sin nombre';
}
